import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useRouterState } from "@tanstack/react-router";
import { Search, Heart, User, ShoppingBag, Menu, X, ShieldCheck } from "lucide-react";
import { useCart, useWishlist } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import logoAsset from "@/assets/royal-lumina-logo.png";

const links: { label: string; to: string }[] = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Collections", to: "/collections" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { count } = useCart();
  const { ids: wishIds } = useWishlist();
  const { role } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isHome = pathname === "/";

  const isAppPage = 
    pathname.startsWith("/shop") || 
    pathname.startsWith("/cart") || 
    pathname.startsWith("/account");

  useEffect(() => {
    if (isAppPage) {
      setMobileOpen(false);
    }
  }, [pathname, isAppPage]);

  useEffect(() => {
    setMounted(true);
    // On non-homepage routes (like /auth), header style is constant (!isHome).
    // Do not attach scroll listener — prevents input focus scrollIntoView layout thrashing.
    if (!isHome) {
      setScrolled(true);
      return;
    }

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 40);
          ticking = false;
        });
        ticking = true;
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? "bg-primary text-cream shadow-luxe"
          : "bg-transparent text-cream"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10 flex items-center justify-between h-20 gap-2">
        <Link to="/" aria-label="Royal Lumina" className="flex items-center min-w-0 shrink-0">
          <img
            src={logoAsset}
            alt="Royal Lumina — Scented Candles"
            className="h-20 sm:h-24 md:h-28 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9 text-[0.78rem] uppercase tracking-[0.18em] font-medium">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="relative py-1 hover:text-gold transition-colors after:absolute after:left-0 after:-bottom-0.5 after:h-px after:w-0 after:bg-gold after:transition-all hover:after:w-full"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-3 shrink-0">
          <Link to="/shop" aria-label="Search" className="h-11 w-11 grid place-items-center hover:text-gold transition-colors">
            <Search className="h-5 w-5" />
          </Link>
          <Link to="/account/wishlist" aria-label="Wishlist" className="relative hidden sm:grid h-11 w-11 place-items-center hover:text-gold transition-colors">
            <Heart className="h-5 w-5" />
            {wishIds.length > 0 && (
              <span className="absolute top-1 right-1 bg-gold text-primary text-[0.6rem] font-bold h-4 w-4 rounded-full grid place-items-center">{wishIds.length}</span>
            )}
          </Link>
          <Link to="/account" aria-label="Account" className="hidden sm:grid h-11 w-11 place-items-center hover:text-gold transition-colors">
            <User className="h-5 w-5" />
          </Link>
          <Link to="/cart" aria-label="Cart" className="relative h-11 w-11 grid place-items-center hover:text-gold transition-colors">
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute top-1 right-1 bg-gold text-primary text-[0.6rem] font-bold h-4 w-4 rounded-full grid place-items-center">{count}</span>
            )}
          </Link>
          {!isAppPage && (
            <button 
              aria-label="Menu" 
              onClick={() => setMobileOpen(true)} 
              className="lg:hidden h-11 w-11 grid place-items-center hover:text-gold cursor-pointer"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Render Mobile Menu Overlay via React Portal onto document.body */}
      {mobileOpen && mounted && createPortal(
        <div className="fixed inset-0 z-[99999] lg:hidden flex justify-end">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-primary/80 backdrop-blur-md transition-opacity animate-in fade-in duration-300" 
            onClick={() => setMobileOpen(false)} 
          />

          {/* Drawer Sidebar */}
          <div className="relative z-10 w-[85%] max-w-sm h-full bg-background text-primary p-6 sm:p-8 flex flex-col shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
            <div className="flex items-center justify-between border-b border-border/80 pb-4">
              <div className="font-display text-2xl tracking-tight text-primary">Royal Lumina</div>
              <button 
                aria-label="Close" 
                onClick={() => setMobileOpen(false)} 
                className="h-10 w-10 rounded-full border border-border grid place-items-center hover:border-gold hover:text-gold transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <nav className="mt-6 flex flex-col divide-y divide-border/60">
              {links.map((l) => (
                <Link 
                  key={l.label} 
                  to={l.to} 
                  onClick={() => setMobileOpen(false)} 
                  className="py-4 font-display text-2xl text-primary hover:text-gold transition-colors flex items-center justify-between"
                >
                  <span>{l.label}</span>
                  <span className="text-xs text-gold font-sans font-medium uppercase tracking-widest">→</span>
                </Link>
              ))}
              {role === "admin" && (
                <Link 
                  to="/admin" 
                  onClick={() => setMobileOpen(false)} 
                  className="py-4 font-display text-xl text-gold hover:text-primary transition-colors flex items-center justify-between"
                >
                  <span>Admin Panel</span>
                  <ShieldCheck className="h-4 w-4 text-gold" />
                </Link>
              )}
            </nav>

            <div className="mt-auto pt-8 border-t border-border/80 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <Link 
                  to="/account" 
                  onClick={() => setMobileOpen(false)} 
                  className="btn-outline-dark justify-center text-xs h-11 px-1 gap-1"
                >
                  <User className="h-4 w-4" /> Account
                </Link>
                <Link 
                  to="/account/wishlist" 
                  onClick={() => setMobileOpen(false)} 
                  className="btn-outline-dark justify-center text-xs h-11 px-1 gap-1"
                >
                  <Heart className="h-4 w-4" /> ({wishIds.length})
                </Link>
                <Link 
                  to="/cart" 
                  onClick={() => setMobileOpen(false)} 
                  className="btn-gold justify-center text-xs h-11 px-1 gap-1"
                >
                  <ShoppingBag className="h-4 w-4" /> ({count})
                </Link>
              </div>
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-center text-muted-foreground pt-2">
                Handcrafted Luxury Scented Candles
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}